import { FunctionComponent, PropsWithChildren, ReactNode } from 'react';
import { twMerge } from 'src/utils/twMerge';

type CommonProps = PropsWithChildren<{
  icon?: ReactNode;
  size?: 'sm' | 'xs';
  isWide?: boolean;
  isTruncated?: boolean;
  className?: string;
  'aria-label'?: string;
  'data-testid'?: string;
}>;

type StaticProps = {
  onClick?: undefined;
  isTouchPropagationStopped?: undefined;
  isDisabled?: undefined;
};

type ClickableProps = {
  onClick: () => void;
  isTouchPropagationStopped?: boolean;
  isDisabled?: boolean;
};

type Props = CommonProps & (StaticProps | ClickableProps);

const BASE_CLASSES =
  'inline-flex items-center gap-1 rounded-full py-1 font-bold';

const SIZE_CLASSES = {
  sm: 'text-sm',
  xs: 'text-xs',
};

const FOCUS_CLASSES =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-darkest';

const Pill: FunctionComponent<Props> = ({
  icon,
  size = 'sm',
  isWide = false,
  isTruncated = false,
  isTouchPropagationStopped = false,
  isDisabled = false,
  className,
  onClick,
  children,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
}) => {
  const classes = twMerge(
    BASE_CLASSES,
    isWide ? 'px-3' : 'px-2',
    SIZE_CLASSES[size],
    className,
  );
  const content = (
    <>
      {icon}
      {isTruncated ? (
        <span
          data-testid={dataTestId && `${dataTestId}-text`}
          className="min-w-0 truncate"
        >
          {children}
        </span>
      ) : (
        children
      )}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        data-testid={dataTestId}
        aria-label={ariaLabel}
        disabled={isDisabled}
        onClick={onClick}
        onTouchStart={
          isTouchPropagationStopped ? e => e.stopPropagation() : undefined
        }
        className={twMerge(classes, FOCUS_CLASSES)}
      >
        {content}
      </button>
    );
  }

  return (
    <span data-testid={dataTestId} aria-label={ariaLabel} className={classes}>
      {content}
    </span>
  );
};

export default Pill;
